// DispatchDeck — Approvals badge (TASK-9657, COD UX: "dispatchers won't look in
// Account for approvals")
// ============================================================================
// `window.ApprovalsBadge` — how many changes Claude (or Ask DispatchDeck) asked
// for that are waiting for THIS person to approve, shown where they work:
//   - an "Approvals N" button in the Dispatch board header and the Maintenance
//     header (any element with [data-approvals-badge] — hosts render it hidden
//     and call paint() after they render);
//   - a count pill on the Account nav entries (#btn-account /
//     #mobile-account-btn); Account has an Approvals button.
// Tapping the button opens the Approvals page, #/approvals (TASK-10249).
// OLD LINKS: every link already handed out reads
// #/account?claude=requests[&proposal=<id>]. forwardLegacy() turns that into
// #/approvals[?proposal=<id>] at load and on every hashchange, BEFORE app.js's
// router reads the hash (this module is imported, and its listener added,
// before app.js's). Forever: those links live in old Claude chats.
//
// SOURCE OF THE COUNT — the existing listMcpProposals callable in count mode
// ({ countOnly: true } -> { approvable }). mcpProposals is server-only in
// firestore.rules (get/list: false), so there is no client listener; the
// server's countApprovable mirrors decideProposal (own rows only, pending, not
// expired, token not revoked, the request's module set incl. ALL modules for
// holds). The owner's reject-only view of other people's rows is NOT counted.
//
// COST — one small call: on sign-in, when the tab comes back to the front, when
// the person opens Dispatch / Maintenance / Account, after an approve/reject,
// and every 5 minutes while the tab is visible. Never per card. Calls closer
// than MIN_GAP_MS reuse the last answer.
//
// WHO — the same people the server would answer (window.carrierAssistantEligible,
// the cosmetic mirror of _resolveCarrierAssistantTenant). Driver-PIN, driver
// link and public-token sessions never call and never see it. Any error = hide,
// quietly (a login with no Claude-eligible module is refused server-side).
//
// NUDGE (TASK-9683 slice 2) — the same reply carries `waitingOnYou` (covered
// Claude requests from coworkers that THIS login could approve as the second
// person) and, for the owner only, `expiredAlerts` (covered clock-outs that
// lapsed unapproved — the shift may still be open). A waiting id is toasted
// ONCE per login on this device (seen ids in localStorage, wrapped: a blocked
// storage falls back to this tab's memory); an expiry alert comes back on every
// page load while the server still reports it (7 days). In-app only: no SMS, no email.
// Toast text is set with textContent by showToast; names come from the server.
//
// LOADING (Pitfall #1): imported ONCE by app.js. Hosts call
// `window.ApprovalsBadge?.paint()` — never an import.
// ============================================================================

import { functions, httpsCallable } from './firebase-config.js?v=1868';

(function () {
  'use strict';

  const TARGET = '#/approvals';

  // TASK-9803 slice 1 — the approve link Claude hands back names ONE request:
  //   #/account?claude=requests&proposal=<id>   (functions/src/utils/approveLinks.js)
  // forwarded to #/approvals?proposal=<id> (TASK-10249). The router rewrites
  // the hash to #/approvals before the lazy page loads (and a sign-in can sit
  // in between), so the id is PARKED here,
  // where it is read before app.js runs (this module is imported once, at
  // boot), and on every hashchange. The card asks for it with
  // proposalTarget(). Parking is navigation only: the card shows that row if
  // the signed-in person's own list has it, and Approve still goes through
  // decideMcpProposal with every server check. Nothing is approved from a URL.
  // Only a plain id is parked (letters and digits, same as the server's
  // PROPOSAL_ID_RE); anything else is ignored.
  const PROPOSAL_PARAM = 'proposal';
  const PROPOSAL_ID_RE = /^[A-Za-z0-9]{1,64}$/;
  const TARGET_TTL_MS = 30 * 60 * 1000;
  // TASK-10249 — #/approvals?proposal=<id>, or the older
  // #/account?claude=requests&proposal=<id>. Anything else: null.
  function hashQuery(hash) {
    const h = String(hash || '');
    const page = /^#\/approvals(?:\?|$)/.test(h) ? 'approvals' : (/^#\/account(?:\?|$)/.test(h) ? 'account' : null);
    if (!page) return null;
    const q = h.indexOf('?') >= 0 ? h.slice(h.indexOf('?') + 1) : '';
    let claude = null; let id = null; let seen = 0;
    for (const part of q ? q.split('&') : []) {
      const eq = part.indexOf('=');
      const k = eq >= 0 ? part.slice(0, eq) : part;
      const v = eq >= 0 ? part.slice(eq + 1) : '';
      if (k === 'claude') claude = v;
      else if (k === PROPOSAL_PARAM) { id = v; seen++; }
    }
    // Two `proposal=` values is not a link we made: ignore both.
    const ok = seen === 1 && PROPOSAL_ID_RE.test(id || '');
    return { page, legacy: page === 'account' && claude === 'requests', id: ok ? id : null };
  }
  function proposalFromHash(hash) {
    const r = hashQuery(hash);
    return r && (r.page === 'approvals' || r.legacy) ? r.id : null;
  }
  // The #/approvals hash an old #/account?claude=requests link stands for, or null.
  function legacyTarget(hash) {
    const r = hashQuery(hash);
    if (!r || !r.legacy) return null;
    return r.id ? `#/approvals?${PROPOSAL_PARAM}=${r.id}` : '#/approvals';
  }
  function forwardLegacy() {
    const to = legacyTarget(window.location.hash);
    if (!to) return;
    try {
      const l = window.location;
      window.history.replaceState(window.history.state, '', `${l.pathname}${l.search}${to}`);
    } catch (_) { window.location.hash = to; }
  }
  let parked = null; // { id, at }
  function parkFromHash() {
    const id = proposalFromHash(window.location.hash);
    if (id) parked = { id, at: Date.now() };
  }
  try { forwardLegacy(); parkFromHash(); } catch (_) { parked = null; }
  window.addEventListener('hashchange', () => {
    try { forwardLegacy(); } catch (_) { /* the router then shows Account */ }
    const h = String(window.location.hash || '');
    // Left Approvals for another view: the parked request is spent.
    if (!/^#\/approvals\b/.test(h)) { parked = null; return; }
    parkFromHash();
  });
  function proposalTarget() {
    if (!parked) return null;
    if (Date.now() - parked.at > TARGET_TTL_MS) { parked = null; return null; }
    return parked.id;
  }
  function clearProposalTarget() { parked = null; }
  const POLL_MS = 5 * 60 * 1000;
  const MIN_GAP_MS = 20 * 1000;
  const NAV_IDS = ['btn-account', 'mobile-account-btn'];

  const S = {
    key: null,        // `${tenant}|${uid}` the count belongs to
    count: 0,
    lastAt: 0,
    inflight: null,
    again: false,     // a forced refresh arrived while one was in flight
    returnTo: null,   // the view the badge was tapped from (Account offers a way back)
    timer: null,
    bound: false,
  };

  function isRestrictedSession() {
    const st = window.AppState || {};
    return !!(st.driverModePin || st.driverMode || st.isPublicAccess || st.accessToken);
  }
  function eligible() {
    const st = window.AppState || {};
    if (isRestrictedSession()) return false;
    if (!st.tenant || !st.user || st.user.isAnonymous) return false;
    if (typeof window.carrierAssistantEligible !== 'function') return false;
    try { return window.carrierAssistantEligible() === true; } catch (_) { return false; }
  }
  function keyNow() {
    const st = window.AppState || {};
    return `${st.tenant || ''}|${(st.user && st.user.uid) || ''}`;
  }

  const _call = httpsCallable(functions, 'listMcpProposals');

  async function refresh(opts) {
    const force = !!(opts && opts.force);
    if (!eligible()) { setCount(0); return 0; }
    const key = keyNow();
    if (key !== S.key) { S.key = key; S.count = 0; S.lastAt = 0; S.again = false; }
    if (!force && S.lastAt && Date.now() - S.lastAt < MIN_GAP_MS) { paint(); return S.count; }
    if (S.inflight) {
      // A forced refresh (right after an approve / reject) must not be answered
      // by a count that started BEFORE the decision (COD Code Reviewer): run
      // one more as soon as this one lands.
      if (force) S.again = true;
      return S.inflight;
    }
    S.inflight = (async () => {
      try {
        const res = await _call({ countOnly: true });
        const n = Number(res && res.data && res.data.approvable);
        // A reply for a session that has since changed is dropped.
        if (keyNow() === key) {
          setCount(Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
          try { nudge(res && res.data, key); } catch (_) { /* a nudge never breaks the count */ }
        }
      } catch (e) {
        // Refused (no Claude-eligible module) = nothing to show. Anything else
        // (network, timeout) keeps the last known count rather than reading as
        // "nothing waiting" (COD Domain).
        const code = String((e && e.code) || '');
        if (keyNow() === key && /permission-denied|unauthenticated|failed-precondition/.test(code)) setCount(0);
      } finally {
        if (keyNow() === key) S.lastAt = Date.now(); // never stamp a newer session's clock
        S.inflight = null;
      }
      if (S.again && keyNow() === key) { S.again = false; return refresh({ force: true }); }
      return S.count;
    })();
    return S.inflight;
  }

  function setCount(n) {
    S.count = n;
    paint();
  }

  // ---- TASK-9683 slice 2: the nudge ---------------------------------------
  const SEEN_MAX = 200;
  const seenMem = new Map(); // login key -> Set (this tab; the fallback when storage is blocked)
  const alertMem = new Map(); // login key -> Set of expiry-alert ids shown in THIS page load
  const seenKey = (key) => `dd_approvals_seen_${key}`;
  function loadSeen(key) {
    const mem = [...(seenMem.get(key) || [])];
    try {
      const raw = window.localStorage.getItem(seenKey(key));
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr.concat(mem) : mem);
    } catch (_) { return new Set(mem); }
  }
  function saveSeen(key, set) {
    const arr = [...set].slice(-SEEN_MAX);
    seenMem.set(key, new Set(arr));
    try { window.localStorage.setItem(seenKey(key), JSON.stringify(arr)); } catch (_) { /* memory only */ }
  }
  const KIND_WORDS = {
    clock_out_person: 'a clock-out', move_clock_time: 'a clock-time change', edit_clock_times: 'a clock-time change',
    add_clock_entry: 'a clock entry', split_clock_entry: 'a clock entry split', create_ticket: 'a new ticket', update_ticket: 'a ticket edit',
    propose_price_book_changes: 'a price book change', propose_item_code_changes: 'an Item Code change',
    propose_invoice_visibility: 'an invoice hide list change', delete_customer: 'deleting a customer', delete_job: 'deleting a job',
  };
  const kindWord = (k) => KIND_WORDS[String(k || '')] || 'a change';
  function nudge(d, key) {
    if (!d || typeof window.showToast !== 'function') return;
    const waiting = (Array.isArray(d.waitingOnYou) ? d.waitingOnYou : []).filter((r) => r && typeof r.id === 'string' && r.id);
    const alerts = (Array.isArray(d.expiredAlerts) ? d.expiredAlerts : []).filter((r) => r && typeof r.id === 'string' && r.id);
    if (!waiting.length && !alerts.length) return;
    const seen = loadSeen(key);
    const freshW = waiting.filter((r) => !seen.has(`w:${r.id}`));
    // The owner's expiry alert is remembered for THIS page load only (COD
    // Domain): it comes back each time DispatchDeck is opened, for the server's
    // 7-day window, because a dismissed toast must not bury an open shift.
    const alertSeen = alertMem.get(key) || new Set();
    alertMem.set(key, alertSeen);
    const freshA = alerts.filter((r) => !alertSeen.has(r.id));
    if (!freshW.length && !freshA.length) return;
    if (freshW.length) {
      const r = freshW[0];
      const msg = freshW.length === 1
        ? `${String(r.requester || 'A coworker').slice(0, 80)}'s Claude asked for ${kindWord(r.kind)}. It needs a second person, and you can approve it.`
        : `${freshW.length} Claude requests from coworkers need a second person, and you can approve them.`;
      window.showToast(msg, 'info', { label: 'Review', callback: () => open() });
      freshW.forEach((x) => seen.add(`w:${x.id}`));
    }
    if (freshA.length) {
      const r = freshA[0];
      const clockOut = String(r.kind || '') === 'clock_out_person';
      const msg = freshA.length === 1
        ? `${clockOut ? 'A clock-out' : 'A clock-time change'} Claude asked for expired with no second approval (${String(r.summary || kindWord(r.kind)).slice(0, 160)}, asked by ${String(r.requester || 'a coworker').slice(0, 80)}). Nothing was changed, so ${clockOut ? 'the shift may still be open' : 'those hours are as they were'}. Check Workforce.`
        : `${freshA.length} clock requests Claude asked for expired with no second approval. Nothing was changed, so shifts may still be open or hours not fixed. Check Workforce.`;
      // Sticky: the owner must act (or dismiss) — an open shift keeps adding hours.
      window.showToast(msg, 'warning', { label: 'Workforce', callback: () => { window.location.hash = '#/workforce'; } }, Infinity);
      freshA.forEach((x) => alertSeen.add(x.id));
    }
    if (freshW.length) saveSeen(key, seen);
  }

  function label(n) {
    return `${n} change${n === 1 ? '' : 's'} waiting for your approval`;
  }

  // Paint every host button + the Account nav pills from the cached count.
  // Cheap and idempotent: hosts call it right after they render their header.
  function paint() {
    injectCss();
    const n = eligible() ? S.count : 0;
    const shown = n > 99 ? '99+' : String(n);
    document.querySelectorAll('[data-approvals-badge]').forEach((btn) => {
      if (!btn.dataset.approvalsBound) {
        btn.dataset.approvalsBound = '1';
        btn.addEventListener('click', open);
      }
      const cnt = btn.querySelector('.dd-approvals-count');
      if (!n) {
        btn.hidden = true;
        btn.style.display = 'none';
        if (cnt) cnt.textContent = '';
        return;
      }
      btn.hidden = false;
      btn.style.display = '';
      if (cnt) cnt.textContent = shown;
      btn.title = `${label(n)} — tap to review`;
      btn.setAttribute('aria-label', `Approvals: ${label(n)}`);
    });
    NAV_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      let pill = el.querySelector('.dd-approvals-nav-badge');
      if (!n) { if (pill) pill.remove(); return; }
      if (!pill) { pill = document.createElement('span'); pill.className = 'dd-approvals-nav-badge'; el.appendChild(pill); }
      pill.textContent = n > 9 ? '9+' : String(n);
      pill.title = label(n);
      pill.setAttribute('aria-label', label(n));
    });
    // Phones: Account lives inside the bottom bar's More menu, so the More
    // button carries the count too (the existing corner .nav-alert-badge style).
    const more = document.getElementById('mobile-nav-more');
    if (more) {
      let dot = more.querySelector('.dd-approvals-more-badge');
      if (!n) { if (dot) dot.remove(); } else {
        if (!dot) { dot = document.createElement('span'); dot.className = 'nav-alert-badge dd-approvals-more-badge'; more.appendChild(dot); }
        dot.textContent = n > 9 ? '9+' : String(n);
        dot.setAttribute('aria-label', label(n));
      }
    }
  }

  function open(ev) {
    if (ev) ev.preventDefault();
    // UX Lead (COD): approving should not strand a dispatcher on Approvals.
    // Remember the board / Maintenance view; the page offers a button back.
    const from = String(window.location.hash || '');
    if (/^#\/(dispatch|maintenance)\b/.test(from)) S.returnTo = from.replace(/\?.*$/, '');
    // TASK-9803 — the badge opens the whole list, not an earlier link's request.
    parked = null;
    if (!/^#\/approvals\b/.test(window.location.hash || '')) window.location.hash = TARGET;
  }

  function onVisible() {
    if (document.visibilityState === 'visible') refresh();
  }
  function onHash() {
    const h = (window.location.hash || '').toLowerCase();
    // Left Approvals: the "Back to …" offer is spent.
    if (!h.startsWith('#/approvals')) S.returnTo = null;
    if (/^#\/(dispatch|maintenance|account|approvals)\b/.test(h)) refresh();
  }

  function start() {
    if (!eligible()) { stop(); return; }
    if (!S.bound) {
      S.bound = true;
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('hashchange', onHash);
    }
    if (!S.timer) {
      S.timer = setInterval(() => {
        if (document.visibilityState === 'visible') refresh();
      }, POLL_MS);
    }
    refresh({ force: true });
  }

  function stop() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
    if (S.bound) {
      S.bound = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('hashchange', onHash);
    }
    S.key = null;
    S.lastAt = 0;
    setCount(0);
  }

  // Self-contained styles: no styles.css buster to keep in sync.
  function injectCss() {
    if (document.getElementById('dd-approvals-css')) return;
    const s = document.createElement('style');
    s.id = 'dd-approvals-css';
    s.textContent = `
      .dd-approvals-btn{position:relative;white-space:nowrap;gap:8px;padding:8px 14px;font-size:0.9rem;border:2px solid var(--brand-blue,#3B82F6) !important;color:var(--text-primary) !important;min-height:44px}
      .dd-approvals-btn .material-icons{color:var(--brand-blue,#3B82F6)}
      .dd-approvals-count{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 6px;border-radius:11px;background:var(--status-danger,#DC2626);color:#fff;font-size:0.8rem;font-weight:700;line-height:1}
      .dd-approvals-nav-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;margin-left:6px;border-radius:9px;background:var(--status-danger,#DC2626);color:#fff;font-size:11px;font-weight:700}
    `;
    document.head.appendChild(s);
  }

  window.ApprovalsBadge = {
    start,
    stop,
    refresh,
    paint,
    open,
    // test hook (read-only)
    // { hash, label } of the view the badge was tapped from, or null.
    returnTo: () => (S.returnTo ? { hash: S.returnTo, label: /^#\/maintenance/.test(S.returnTo) ? 'Maintenance' : 'Dispatch' } : null),
    // TASK-9803 S1 — the one request an approve link named (or null), and
    // how the card says it is done with it.
    proposalTarget,
    clearProposalTarget,
    _proposalFromHash: proposalFromHash,
    _legacyTarget: legacyTarget,
    _state: () => ({ count: S.count, key: S.key, eligible: eligible() }),
    _nudge: nudge,
  };
})();
